/**
 * Uma tabela num `.xlsx` — o arquivo que o Windows abre no Excel (13/10/2026).
 *
 * ── Por que não CSV ───────────────────────────────────────────────────────
 *
 * O CSV abria com o programa que o sistema associa a `.csv`, e numa máquina
 * com o VS Code instalado esse programa é o VS Code: a pessoa clicava em
 * "Exportar" e via texto com ponto e vírgula. O `.xlsx` é do Excel em qualquer
 * Windows com Office. E ele resolve de outro jeito as três armadilhas do CSV
 * brasileiro (ver `planilha.ts`): número é número na célula, sem depender da
 * vírgula do Excel em português; o texto é UTF-8 por definição; e texto é
 * sempre texto — célula `inlineStr` nunca vira fórmula, comece com `=` ou não.
 *
 * ── Por que sem biblioteca ────────────────────────────────────────────────
 *
 * Um `.xlsx` é um `.zip` com cinco XMLs. As bibliotecas de planilha resolvem
 * leitura, estilos, fórmulas e várias abas, e nada disso é usado por uma
 * exportação de uma aba só. Sobraria o custo: dependência no `npm audit`, na
 * licença e no tamanho do que o navegador baixa. O zip vai sem compressão
 * (`stored`), que todo leitor aceita e dispensa o algoritmo de deflate.
 */

/** Uma célula: texto, número, ou vazia. */
export type Celula = string | number | null | undefined;

/** As linhas de uma aba, de cima para baixo. Linha vazia é linha em branco. */
export type Tabela = readonly (readonly Celula[])[];

export const TIPO_XLSX = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

// ── Os XMLs ─────────────────────────────────────────────────────────────────

const CABECALHO_XML = '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>\n';
const NS_PRINCIPAL = 'http://schemas.openxmlformats.org/spreadsheetml/2006/main';
const NS_RELACOES = 'http://schemas.openxmlformats.org/officeDocument/2006/relationships';
const NS_PACOTE = 'http://schemas.openxmlformats.org/package/2006/relationships';

/**
 * Texto seguro dentro do XML.
 *
 * Os cinco escapes, e os caracteres de controle fora: o XML 1.0 não os aceita,
 * e um só deles no nome de um cliente faz o Excel recusar o arquivo inteiro
 * ("encontramos um problema no conteúdo").
 */
function xml(texto: string): string {
  return (
    texto
      // eslint-disable-next-line no-control-regex -- é exatamente o que sai.
      .replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F]/g, '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
  );
}

/** `0` → `A`, `25` → `Z`, `26` → `AA`. */
export function letraDaColuna(indice: number): string {
  let letra = '';
  let n = indice + 1;
  while (n > 0) {
    const resto = (n - 1) % 26;
    letra = String.fromCharCode(65 + resto) + letra;
    n = Math.floor((n - 1) / 26);
  }
  return letra;
}

function celula(valor: Celula, referencia: string): string {
  if (valor === null || valor === undefined || valor === '') return '';
  if (typeof valor === 'number') {
    // Infinito e NaN não são número de planilha: a célula fica vazia.
    return Number.isFinite(valor) ? `<c r="${referencia}"><v>${valor}</v></c>` : '';
  }
  return `<c r="${referencia}" t="inlineStr"><is><t xml:space="preserve">${xml(valor)}</t></is></c>`;
}

/**
 * A largura de cada coluna, pelo texto mais longo dela.
 *
 * Sem isto o Excel abre todas com a largura padrão, e "Espera mediana pela
 * primeira resposta (minutos)" aparece cortado. Com teto, para uma descrição
 * longa não empurrar o resto da planilha para fora da tela.
 */
function larguras(tabela: Tabela): string {
  const maiores: number[] = [];
  for (const linha of tabela) {
    linha.forEach((valor, i) => {
      const tamanho = valor === null || valor === undefined ? 0 : String(valor).length;
      maiores[i] = Math.max(maiores[i] ?? 0, tamanho);
    });
  }
  if (maiores.length === 0) return '';
  const colunas = maiores.map((tamanho, i) => {
    const largura = Math.min(60, Math.max(10, tamanho + 2));
    return `<col min="${i + 1}" max="${i + 1}" width="${largura}" customWidth="1"/>`;
  });
  return `<cols>${colunas.join('')}</cols>`;
}

function aba(tabela: Tabela): string {
  const linhas = tabela.map((linha, i) => {
    const numero = i + 1;
    const celulas = linha.map((valor, j) => celula(valor, `${letraDaColuna(j)}${numero}`)).join('');
    return celulas ? `<row r="${numero}">${celulas}</row>` : '';
  });
  return (
    `${CABECALHO_XML}<worksheet xmlns="${NS_PRINCIPAL}">${larguras(tabela)}` +
    `<sheetData>${linhas.join('')}</sheetData></worksheet>`
  );
}

/**
 * O nome da aba, dentro das regras do Excel: até 31 caracteres, e sem
 * `[ ] : * ? / \`. Fora delas ele recusa o arquivo.
 */
function nomeDaAba(nome: string): string {
  const limpo = nome
    .replace(/[[\]:*?/\\]/g, ' ')
    .trim()
    .slice(0, 31);
  return limpo || 'Planilha';
}

function arquivosDoPacote(tabela: Tabela, nome: string): [string, string][] {
  return [
    [
      '[Content_Types].xml',
      `${CABECALHO_XML}<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">` +
        '<Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>' +
        '<Default Extension="xml" ContentType="application/xml"/>' +
        '<Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>' +
        '<Override PartName="/xl/worksheets/sheet1.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>' +
        '<Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/>' +
        '</Types>',
    ],
    [
      '_rels/.rels',
      `${CABECALHO_XML}<Relationships xmlns="${NS_PACOTE}">` +
        '<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/>' +
        '</Relationships>',
    ],
    [
      'xl/workbook.xml',
      `${CABECALHO_XML}<workbook xmlns="${NS_PRINCIPAL}" xmlns:r="${NS_RELACOES}">` +
        `<sheets><sheet name="${xml(nomeDaAba(nome))}" sheetId="1" r:id="rId1"/></sheets>` +
        '</workbook>',
    ],
    [
      'xl/_rels/workbook.xml.rels',
      `${CABECALHO_XML}<Relationships xmlns="${NS_PACOTE}">` +
        '<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet1.xml"/>' +
        '<Relationship Id="rId2" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/>' +
        '</Relationships>',
    ],
    // O mínimo que o Excel espera de estilos: uma fonte, os dois preenchimentos
    // obrigatórios, uma borda e o estilo "Normal".
    [
      'xl/styles.xml',
      `${CABECALHO_XML}<styleSheet xmlns="${NS_PRINCIPAL}">` +
        '<fonts count="1"><font><sz val="11"/><name val="Calibri"/></font></fonts>' +
        '<fills count="2"><fill><patternFill patternType="none"/></fill><fill><patternFill patternType="gray125"/></fill></fills>' +
        '<borders count="1"><border><left/><right/><top/><bottom/><diagonal/></border></borders>' +
        '<cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs>' +
        '<cellXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0"/></cellXfs>' +
        '<cellStyles count="1"><cellStyle name="Normal" xfId="0" builtinId="0"/></cellStyles>' +
        '</styleSheet>',
    ],
    ['xl/worksheets/sheet1.xml', aba(tabela)],
  ];
}

// ── O zip ───────────────────────────────────────────────────────────────────

const TABELA_CRC = (() => {
  const tabela = new Uint32Array(256);
  for (let n = 0; n < 256; n += 1) {
    let c = n;
    for (let k = 0; k < 8; k += 1) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    tabela[n] = c >>> 0;
  }
  return tabela;
})();

export function crc32(bytes: Uint8Array): number {
  let crc = 0xffffffff;
  for (const byte of bytes) crc = TABELA_CRC[(crc ^ byte) & 0xff]! ^ (crc >>> 8);
  return (crc ^ 0xffffffff) >>> 0;
}

/**
 * Um zip sem compressão, no formato que todo leitor aceita.
 *
 * Cada arquivo leva um cabeçalho local, e no fim vão o diretório central e o
 * registro de fim. Data fixa em 01/01/1980 (a mínima do formato): o conteúdo é
 * o que importa, e data variável faria o mesmo relatório gerar bytes diferentes.
 */
export function zipSemCompressao(
  arquivos: readonly [string, Uint8Array][],
): Uint8Array<ArrayBuffer> {
  const codificar = new TextEncoder();
  const partes: Uint8Array[] = [];
  const central: Uint8Array[] = [];
  let deslocamento = 0;

  for (const [nome, conteudo] of arquivos) {
    const nomeEmBytes = codificar.encode(nome);
    const crc = crc32(conteudo);

    const local = new DataView(new ArrayBuffer(30));
    local.setUint32(0, 0x04034b50, true); // assinatura do cabeçalho local
    local.setUint16(4, 20, true); // versão para extrair
    local.setUint16(6, 0, true); // flags
    local.setUint16(8, 0, true); // método: stored
    local.setUint16(10, 0, true); // hora
    local.setUint16(12, 0x21, true); // data: 01/01/1980
    local.setUint32(14, crc, true);
    local.setUint32(18, conteudo.length, true);
    local.setUint32(22, conteudo.length, true);
    local.setUint16(26, nomeEmBytes.length, true);
    local.setUint16(28, 0, true);
    partes.push(new Uint8Array(local.buffer), nomeEmBytes, conteudo);

    const registro = new DataView(new ArrayBuffer(46));
    registro.setUint32(0, 0x02014b50, true); // assinatura do diretório central
    registro.setUint16(4, 20, true); // versão que criou
    registro.setUint16(6, 20, true); // versão para extrair
    registro.setUint16(8, 0, true);
    registro.setUint16(10, 0, true);
    registro.setUint16(12, 0, true);
    registro.setUint16(14, 0x21, true);
    registro.setUint32(16, crc, true);
    registro.setUint32(20, conteudo.length, true);
    registro.setUint32(24, conteudo.length, true);
    registro.setUint16(28, nomeEmBytes.length, true);
    registro.setUint16(30, 0, true); // extra
    registro.setUint16(32, 0, true); // comentário
    registro.setUint16(34, 0, true); // disco
    registro.setUint16(36, 0, true); // atributos internos
    registro.setUint32(38, 0, true); // atributos externos
    registro.setUint32(42, deslocamento, true);
    central.push(new Uint8Array(registro.buffer), nomeEmBytes);

    deslocamento += 30 + nomeEmBytes.length + conteudo.length;
  }

  const tamanhoCentral = central.reduce((total, parte) => total + parte.length, 0);
  const fim = new DataView(new ArrayBuffer(22));
  fim.setUint32(0, 0x06054b50, true); // assinatura do registro de fim
  fim.setUint16(8, arquivos.length, true);
  fim.setUint16(10, arquivos.length, true);
  fim.setUint32(12, tamanhoCentral, true);
  fim.setUint32(16, deslocamento, true);

  const todas = [...partes, ...central, new Uint8Array(fim.buffer)];
  const saida = new Uint8Array(new ArrayBuffer(todas.reduce((t, p) => t + p.length, 0)));
  let posicao = 0;
  for (const parte of todas) {
    saida.set(parte, posicao);
    posicao += parte.length;
  }
  return saida;
}

/** A tabela como os bytes de um `.xlsx` de uma aba, com o `nome` na aba. */
export function xlsx(tabela: Tabela, nome: string): Uint8Array<ArrayBuffer> {
  const codificar = new TextEncoder();
  return zipSemCompressao(
    arquivosDoPacote(tabela, nome).map(([caminho, texto]) => [caminho, codificar.encode(texto)]),
  );
}
